import {
  midiNoteName,
  midiNoteOctave,
  midiSolfege,
  noteNameToMidi,
} from '@muzly/voice-engine';

import type { TFunction } from 'i18next';

/**
 * 화면에 음 하나를 쓰는 **유일한** 길 — ko `라♯2`(계이름 + 옥타브), en `A#2`.
 * 어느 쪽을 쓸지는 로케일 문구 `web.note.label`이 정한다.
 *
 * **계이름과 음이름을 같이 적지 않는다.** `라♯(A#2)` 는 한 줄에 같은 정보를 두 번 쓰고,
 * 그 폭이 곡선 그래프의 축 열을 두 배로 불린다(`PitchTraceGraph`). 옥타브 숫자는 남는다 —
 * 축에 `도` 가 두 줄 있으면 어느 도인지 화면이 말해 주지 못한다.
 *
 * MIDI(연속값)는 가장 가까운 반음으로 반올림한다 — 실제 f0는 `219.98`처럼 떨어지므로
 * 반올림 없이는 라벨이 반음마다 두 값 사이를 튄다. 음이름 문자열(서버가 준 `F#2`)은
 * 읽어서 계이름을 붙이고, 못 읽으면 받은 그대로 쓴다.
 */
export const noteLabel = (t: TFunction, note: number | string): string => {
  if (typeof note === 'number') {
    return t('web.note.label', noteParams(note));
  }
  const midi = noteNameToMidi(note);
  return midi == null
    ? note
    : t('web.note.label', {
        note: note.trim(),
        solfege: midiSolfege(midi),
        octave: midiOctave(midi),
      });
};

/** `noteLabel`의 재료 — 문장 안에 음을 끼우는 키(`$t(web.note.label)` 중첩)에 넘긴다. */
export const noteParams = (
  midi: number,
): { note: string; solfege: string; octave: number } => ({
  note: midiNoteName(Math.round(midi)),
  solfege: midiSolfege(midi),
  octave: midiOctave(midi),
});

/** 과학적 음높이 표기의 옥타브 — 정의는 `voice-engine`의 음이름 계산과 한 곳에 둔다. */
const midiOctave = (midi: number): number => midiNoteOctave(Math.round(midi));
